import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { endOfDay, isAfter, isSameDay } from 'date-fns';
import { AppointmentModel } from '@devexpress/dx-react-scheduler';
import {
  useAddScheduleRangeMutation,
  useDeleteScheduleRangeMutation,
  useUpdateScheduleRangeMutation,
} from 'store/scheduleRanges/scheduleRanges';
import { useAppDispatch } from 'store/hooks';
import { setErrorCatch, setSuccessStatus } from 'store/status/actions';
import extractTimeFromDate from 'helpers/extractTimeFromDate';
import { millisecondsInSecond, wholeDayInSeconds } from 'helpers/timeConsts';
import checkIsDateMidnight from 'helpers/checkIsDateMidnight';
import { useUpdateScheduleMutation } from 'store/schedules/schedules';
import { Schedule } from 'store/schedules/interfaces';
import {
  CountValue,
  ScheduleCalendarContext,
  UpdateWorkScheduleParams,
} from './WorkScheduleCalendar.context';

export interface WorkScheduleCalendarContextProps {
  children: React.ReactNode;
  schedule: Schedule;
  initWorkSchedules?: AppointmentModel[];
  onScheduleEdit?: (schedule: Schedule) => void;
}

export const draftWorkScheduleId = 'draft-work-schedule';

export default function WorkScheduleCalendarProvider({
  children,
  schedule,
  initWorkSchedules = [],
  onScheduleEdit,
}: WorkScheduleCalendarContextProps) {
  const dispatch = useAppDispatch();

  const [workSchedules, setWorkSchedules] =
    useState<AppointmentModel[]>(initWorkSchedules);
  // state for feature draftWorkSchedule on adding resize
  const [isDraftCreating, setIsDraftCreating] = useState(false);
  const [draftAnchorEl, setDraftAnchorEl] = useState<HTMLDivElement | null>(
    null,
  );

  const [addScheduleRange] = useAddScheduleRangeMutation();
  const [deleteScheduleRange] = useDeleteScheduleRangeMutation();
  const [updateScheduleRange] = useUpdateScheduleRangeMutation();
  const [updateSchedule, { isLoading: scheduleLoading }] =
    useUpdateScheduleMutation();

  const handleAddDraftWorkSchedule = useCallback(
    (scheduleWorkingHours: AppointmentModel) => {
      const isDraftScheduleExist = workSchedules.some(
        (scheduleItem) => scheduleItem.id === draftWorkScheduleId,
      );

      if (isDraftScheduleExist) {
        return;
      }

      setWorkSchedules((prev) => [
        ...prev,
        {
          ...scheduleWorkingHours,
          id: draftWorkScheduleId,
        },
      ]);
    },
    [workSchedules],
  );

  const handleRemoveWorkSchedule = useCallback(
    async (id: AppointmentModel['id']) => {
      const draftScheduleToDelete = workSchedules.find(
        (scheduleItem) => scheduleItem.id === id,
      );

      if (!draftScheduleToDelete) {
        return;
      }

      try {
        if (
          typeof draftScheduleToDelete.id === 'string' &&
          draftScheduleToDelete.id !== draftWorkScheduleId
        ) {
          await deleteScheduleRange(draftScheduleToDelete.id).unwrap();
          dispatch(setSuccessStatus(true));
        }
        setWorkSchedules((prev) =>
          prev.filter((scheduleItem) => scheduleItem.id !== id),
        );
      } catch (error) {
        dispatch(setErrorCatch(error));
      }
    },
    [deleteScheduleRange, dispatch, workSchedules],
  );

  const updateEndDateOfDraftWorkSchedule = useCallback((newEndDate: Date) => {
    setWorkSchedules((prev) =>
      prev.map((scheduleItem) => {
        if (scheduleItem.id !== draftWorkScheduleId) {
          return scheduleItem;
        }
        const isEndDateSameDay = isSameDay(newEndDate, scheduleItem.startDate);
        const isEndDateAfterStartDate = isAfter(
          newEndDate,
          scheduleItem.startDate,
        );

        if (!isEndDateSameDay || !isEndDateAfterStartDate) {
          return scheduleItem;
        }

        const isEndDateMidnight = checkIsDateMidnight(newEndDate);
        const timeTo = isEndDateMidnight
          ? wholeDayInSeconds
          : extractTimeFromDate(newEndDate) / millisecondsInSecond;

        return {
          ...scheduleItem,
          endDate: isEndDateMidnight ? endOfDay(newEndDate) : newEndDate,
          timeTo,
        };
      }),
    );
  }, []);

  const handleAddDraftScheduleToWorkSchedules = useCallback(async () => {
    try {
      if (!schedule) {
        return;
      }

      const draftWorkSchedule = workSchedules.find(
        (scheduleItem) => scheduleItem.id === draftWorkScheduleId,
      );

      if (
        !(draftWorkSchedule?.endDate instanceof Date) ||
        !(draftWorkSchedule?.startDate instanceof Date)
      ) {
        return;
      }

      const isEndDateMidnight = checkIsDateMidnight(draftWorkSchedule.endDate);

      const timeTo = isEndDateMidnight
        ? wholeDayInSeconds
        : extractTimeFromDate(draftWorkSchedule.endDate) / millisecondsInSecond;
      const timeFrom =
        extractTimeFromDate(draftWorkSchedule.startDate) / millisecondsInSecond;

      const createdSchedule = await addScheduleRange({
        schedule: schedule['@id'],
        day: draftWorkSchedule.day,
        timeFrom,
        timeTo,
      }).unwrap();

      setWorkSchedules((prev) =>
        prev.map((scheduleItem) => {
          if (scheduleItem.id !== draftWorkScheduleId) {
            return scheduleItem;
          }

          const { id, ...restSchedule } = scheduleItem;
          return {
            ...restSchedule,
            id: createdSchedule.id,
            timeTo,
            timeFrom,
          };
        }),
      );
      dispatch(setSuccessStatus(true));
      setDraftAnchorEl(null);
    } catch (error) {
      dispatch(setErrorCatch(error));
    }
  }, [addScheduleRange, dispatch, schedule, workSchedules]);

  const handleUpdateWorkSchedule = useCallback(
    async ({
      schedule: schedulePayload,
      triggerApiCall = false,
      onSuccess = () => {},
    }: UpdateWorkScheduleParams) => {
      try {
        const { id, startDate, endDate, ...restSchedule } = schedulePayload;
        if (typeof id !== 'string') {
          return;
        }
        const isEndDateMidnight = checkIsDateMidnight(endDate as Date);

        const timeTo = isEndDateMidnight
          ? wholeDayInSeconds
          : extractTimeFromDate(endDate as Date) / millisecondsInSecond;
        const timeFrom =
          extractTimeFromDate(startDate as Date) / millisecondsInSecond;

        if (id !== draftWorkScheduleId && triggerApiCall) {
          await updateScheduleRange({
            id,
            timeFrom,
            timeTo,
          }).unwrap();
          dispatch(setSuccessStatus(true));
        }

        setWorkSchedules((prev) =>
          prev.map((scheduleItem) => {
            if (scheduleItem.id !== id) {
              return scheduleItem;
            }

            return {
              ...restSchedule,
              startDate,
              endDate: isEndDateMidnight
                ? endOfDay(scheduleItem.endDate as Date)
                : endDate,
              timeFrom,
              timeTo,
              id,
            };
          }),
        );
        onSuccess();
      } catch (error) {
        dispatch(setErrorCatch(error));
      }
    },
    [dispatch, updateScheduleRange],
  );

  const handleIncreaseScheduleDays = useCallback(
    async (countValue: CountValue) => {
      try {
        if (scheduleLoading) {
          return;
        }
        const updatedSchedule = await updateSchedule({
          id: schedule.id,
          numberOfDays: schedule.numberOfDays + countValue,
        }).unwrap();
        if (onScheduleEdit) {
          onScheduleEdit(updatedSchedule);
        }
        dispatch(setSuccessStatus(true));
      } catch (error) {
        dispatch(setErrorCatch(error));
      }
    },
    [dispatch, onScheduleEdit, schedule, scheduleLoading, updateSchedule],
  );

  const handleDecreaseScheduleDays = useCallback(
    async (countValue: CountValue) => {
      try {
        if (scheduleLoading) {
          return;
        }
        const updatedSchedule = await updateSchedule({
          id: schedule.id,
          numberOfDays: schedule.numberOfDays - countValue,
        }).unwrap();
        if (onScheduleEdit) {
          onScheduleEdit(updatedSchedule);
        }
        dispatch(setSuccessStatus(true));
      } catch (error) {
        dispatch(setErrorCatch(error));
      }
    },
    [dispatch, onScheduleEdit, schedule, updateSchedule, scheduleLoading],
  );

  useEffect(() => {
    setWorkSchedules(initWorkSchedules);
  }, [initWorkSchedules]);

  const contextValue = useMemo(
    () => ({
      schedule,
      workSchedules,
      isDraftCreating,
      draftAnchorEl,
      handleAddDraftWorkSchedule,
      updateEndDateOfDraftWorkSchedule,
      setIsDraftCreating,
      handleAddDraftScheduleToWorkSchedules,
      handleRemoveWorkSchedule,
      handleUpdateWorkSchedule,
      setDraftAnchorEl,
      handleIncreaseScheduleDays,
      handleDecreaseScheduleDays,
    }),
    [
      schedule,
      workSchedules,
      isDraftCreating,
      draftAnchorEl,
      handleAddDraftWorkSchedule,
      updateEndDateOfDraftWorkSchedule,
      handleAddDraftScheduleToWorkSchedules,
      handleRemoveWorkSchedule,
      handleUpdateWorkSchedule,
      setDraftAnchorEl,
      handleIncreaseScheduleDays,
      handleDecreaseScheduleDays,
    ],
  );

  return (
    <ScheduleCalendarContext.Provider value={contextValue}>
      {children}
    </ScheduleCalendarContext.Provider>
  );
}
